/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import config from 'js/app/config';

import { breakpoints, useTheme } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';

import LazyImgix from 'bundles/page/components/shared/LazyImgix';
import type { ArticlesHighlightBody } from 'bundles/unified-seo-content-common/__generated__/baseContentfulTypes';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import RichText from 'bundles/unified-seo-content-common/components/rich-text/RichText';

const styles = {
  highlightOuter: css({
    position: 'relative',
    width: '100%',
  }),
  highlightContainer: (theme: Theme) =>
    css({
      backgroundColor: 'var(--cds-color-purple-25)',
      borderRadius: 4,
      boxShadow: '0px 2px 4px var(--cds-color-darken-100)',
      margin: '45px 0',
      padding: '36px 64px 34px',
      position: 'relative',
      width: '100%',
      zIndex: 2,
      [breakpoints.down('sm')]: {
        padding: '34px 24px',
      },
    }),
  highlightBody: (theme: Theme) =>
    css({
      zIndex: 2,
      h3: {
        ...theme.typography.h2semibold,
        color: 'var(--cds-color-purple-950)',
        marginTop: 16,
        marginBottom: 6,
        [breakpoints.down('sm')]: {
          marginBottom: 16,
        },
        '&:first-of-type': {
          marginTop: 0,
        },
      },
      p: {
        ...theme.typography.h2,
        marginTop: '10px',
      },
      a: {
        textDecoration: 'underline',
      },
    }),
  purplePattern: css({
    bottom: 0,
    margin: '0 -22px -27px 0',
    position: 'absolute',
    right: 0,
    zIndex: 1,
  }),
};

type Props = {
  body: Partial<ArticlesHighlightBody>;
};

const PURPLE_PATTERN = `${config.url.resource_assets}articles/purple-hash-pattern.svg`;

const ArticleHighlight = ({ body }: Props) => {
  const theme = useTheme();
  return (
    <div className="rc-ArticleHighlight cds">
      <div css={styles.highlightOuter}>
        <div css={styles.highlightContainer(theme)}>
          <div css={styles.highlightBody(theme)}>
            <RichText content={body?.json} />
          </div>
        </div>
        <div css={styles.purplePattern}>
          <LazyImgix alt="" src={PURPLE_PATTERN} maxHeight={152} maxWidth={252} />
        </div>
      </div>
    </div>
  );
};

export default ArticleHighlight;
